import { AppState } from "Redux/appSlice";
import {
  Module,
  ModuleState,
  QuestionMap,
  SelectedStep,
  Trigger,
  TriggerBody,
} from "types/type.module";
import translateModule, { mapModule } from "../Logic/ModuleTranslator";

/***
    @function intitializeStep
    initialize module, extract steps, triggers, and selected steps
    @param {object} module - is an object containeing the module steps
    @returns {object}
 */
export default function initializeStep(moduleToTranslate: Module) {
  const mappedModule = mapModule(moduleToTranslate);
  const module = translateModule(moduleToTranslate);

  const result: ModuleState["initialization"] = module.chapters.reduce(
    (acc: ModuleState["initialization"], chapter) => {
      return chapter.questions.reduce(
        (acc, question) => {
          const alwaysTrue = isAlwaysTrue(question.trigger, {
            isAlwaysTrue: false,
          });
          const isEmpty = false; //isTriggerEmpty(q.trigger, { isEmpty: false });
          const isSelected = alwaysTrue.isAlwaysTrue || isEmpty;
          if (isSelected) {
            acc.selectedSteps.push({
              step: question.question_id,
              iterated: false,
              hidden: question.should_be_hidden,
            });
            acc.allSelectedSteps.push({
              step: question.question_id,
              iterated: false,
              hidden: question.should_be_hidden,
            });
          }

          if (question.question_id) {
            acc.steps[question.question_id] = {
              trigger: isSelected
                ? "always"
                : JSON.parse(JSON.stringify(question.trigger)),
              selected: isSelected,
            };
          }

          return acc;
        },
        {
          ...acc,
          steps: { ...acc.steps },
          selectedSteps: [...acc.selectedSteps],
          allSelectedSteps: [...acc.allSelectedSteps],
        }
      );
    },
    {
      steps: {},
      selectedSteps: [],
      allSelectedSteps: [],
      triggers: {},
      currentChapter: 0,
    }
  );

  result.selectedSteps.push({ step: "toPrint", iterated: false });

  Object.keys(result.steps).map((stepName) => {
    const step = result.steps[stepName];

    return extractTriggers(step.trigger, result.triggers, stepName);
  });

  if (result.selectedSteps[0].step) {
    result.currentChapter =
      mappedModule.questions[result.selectedSteps[0].step]?.chapterPos;
  }

  return { initialization: result, map: mappedModule };
}

/**
   @function validateCondition
   validate condtion of trigger and change trigger if it's activated
   @param {object} condition -  object containing logical condition of trigger,
   @param {object} trigger -  name of the trigger that is being activated
   @returns {boolean} condition is true or false
*/
export function validateCondition(
  condition: Trigger | TriggerBody,
  trigger: Trigger | string
) {
  const operation = extractLogicalOperation(condition);
  if (operation.operation === "and") {
    const values = operation.elements?.map((element: any) => {
      if (typeof element !== "string" && "answer" in element) {
        if (element.answer === trigger) {
          element.value = true;
        }
        if (element.negated) {
          return !element.value;
        }
        return element.value;
      } else if (typeof element !== "string" && "question" in element) {
        if (element.question === trigger) {
          element.value = true;
        }
        if (element.negated) {
          return !element.value;
        }
        return element.value;
      } else {
        return validateCondition(element, trigger);
      }
    });
    let result = true;
    values?.forEach((value: any) => (result = result && !!value));
    return result;
  } else if (operation.operation === "or") {
    const values = operation.elements?.map((element: any) => {
      if (typeof element !== "string" && "answer" in element) {
        if (element.answer === trigger) {
          element.value = !element.value;
        }
        if (element.negated) {
          return !element.value;
        }
        return element.value;
      } else if (typeof element !== "string" && "question" in element) {
        if (element.question === trigger) {
          element.value = true;
        }
        if (element.negated) {
          return !element.value;
        }
        return element.value;
      } else {
        return validateCondition(element, trigger);
      }
    });
    let result = false;

    values?.forEach((value: any) => (result = result || !!value));

    return result;
  }
}

/**
   @function extractTriggers
   extract the triggers from the step contidition
   @param {object} trigger - an object containing the triggers
   @param {object} result - the result that is passed in the recursion
   @param {object} stepName - conditions step name
   @returns {object} - of extracted triggers
 */
function extractTriggers(trigger: Trigger, result: any, stepName: string) {
  const triggerAction = (element: any, result: any, stepName: any) => {
    if (typeof element === "object" && "question" in element) {
      const triggerName = element.answer ? element.answer : element.question;
      const previousSteps =
        triggerName in result ? result[triggerName].steps : [];
      result[triggerName] = {
        steps: [stepName, ...previousSteps],
        selected: false,
      };
    } else {
      return extractTriggers(element, result, stepName);
    }
  };
  if (trigger === "always") {
    return result;
  }
  if (Object.keys(trigger).length === 0) {
    return result;
  }
  return navigateTriggers(trigger, result, stepName, triggerAction);
}

/**
   @function getTrueTriggers
   @param {object} trigger - an object containing the triggers
   @param {object} result - the result that is passed in the recursion
*/
export function getTrueTriggers(trigger: Trigger, result: any) {
  const triggerAction = (element: any, result: any) => {
    if (typeof element === "object" && "key" in element) {
      const triggerName = element.key;
      if (element.value) {
        result[triggerName] = element;
      }
    } else {
      return getTrueTriggers(element, result);
    }
  };
  if (trigger === "always") {
    return result;
  }
  return navigateTriggers(trigger, result, null, triggerAction);
}

/**
   @function isAlwaysTrue
   check if condition is always true
   @param {object} trigger - an object containing the condition
   @param {object} result - an object containing the result for the recursion
   @returns {boolean}
 */

// TODO: refactor this function so that it doesn't mutate the result object from input parameter
function isAlwaysTrue(trigger: Trigger, result: any) {
  const triggerAction = (element: any, result: any) => {
    if (typeof element === "object" && Object.keys(element).length > 0) {
      if (!("question" in element)) {
        isAlwaysTrue(element, result);
      }
    } else if (element === "always") {
      result.isAlwaysTrue = true;
    }
  };
  if (trigger === "always") {
    result.isAlwaysTrue = true;
    return result;
  }
  return navigateTriggers(trigger, result, null, triggerAction);
}

/** 
    @function translateNamesInTriggers
    translate name of the triggers in the condition
    @param {object} trigger - object containing the condition
    @returns {none}
*/
export function translateNamesInTriggers(trigger: Trigger) {
  const triggerAction = (element: any) => {
    if (typeof element === "object") {
      if ("chapter" in element) {
        element.key = generateTriggerName(element);
        delete element.chapter;
        delete element.answer;
        delete element.question;
      } else {
        translateNamesInTriggers(element);
      }
    }
  };
  return navigateTriggers(trigger, null, null, triggerAction);
}

/**
   @function isTriggerInCondition
   returns if trigger is in condition
   @param trigger {object} - trigger to search for
   @param condition {object} - condition to search
   @returns {boolean}
*/
export function isTriggerInConditionAndAddDeleteCallBack(
  trigger: {
    [key: string]: unknown;
  },
  condition: {
    [key: string]: unknown;
  },
  result: {
    isAlwaysTrue: boolean;
  }
): boolean {
  const triggerAction = (
    element: any,
    recursiveResult: any,
    stepName: any,
    previousTrigger: any
  ) => {
    if (typeof element === "object" && "chapter" in element) {
      if (
        element.chapter === trigger.chapter &&
        element.question === trigger.question &&
        element.answer === trigger.answer
      ) {
        recursiveResult.result = true;

        recursiveResult.callback = () => {
          if ("and" in previousTrigger) {
            previousTrigger.and = previousTrigger.and.filter(
              (t: any) =>
                t.chapter !== trigger.chapter &&
                t.question !== trigger.question &&
                t.answer !== trigger.answer
            );
          }
          if ("or" in previousTrigger) {
            previousTrigger.or = previousTrigger.or.filter(
              (t: any) =>
                t.chapter !== trigger.chapter &&
                t.question !== trigger.question &&
                t.answer !== trigger.answer
            );
          }
        };
      }
    } else {
      return isTriggerInConditionAndAddDeleteCallBack(
        trigger,
        element,
        recursiveResult
      );
    }
  };
  return navigateTriggers(condition, result, null, triggerAction);
}

/**
   @function navigateTriggers
   help function to navigate all conditions
   @param {object} trigger - an object that contains the conditions of the step
   @param {object} result - the result object for the recursion
   @returns {object}
 */
export function navigateTriggers(
  trigger: Trigger | undefined,
  result: {
    isAlwaysTrue: boolean;
  } | null,
  stepName: string | null,
  triggerAction: (
    element: any,
    result?: any,
    stepName?: any,
    trigger?: Trigger
  ) => void
): any {
  const operation = extractLogicalOperation(trigger);
  if (Array.isArray(operation.elements)) {
    operation.elements.map((element: any) => {
      triggerAction(element, result, stepName, trigger);
    });
  } else {
    return navigateTriggers(
      operation.elements,
      result,
      stepName,
      triggerAction
    );
  }
  return result;
}

/**
   @function extractLogicalOperation
   extract logical operations from the trigger
   @param {object} trigger - trigger object to extract
   @returns {object} - containing operation and elements
 */
export function extractLogicalOperation(
  trigger: Trigger | TriggerBody | undefined
): any {
  // TODO: how this should behave because trigger can be a string
  if (!trigger || typeof trigger === "string") {
    return { operation: null, elements: [] };
  }

  const operations = Object.keys(trigger);
  if (operations.length === 0) {
    return { operation: null, elements: [] };
  } else {
    const operation = operations[0];
    return {
      operation,
      elements: operation in trigger ? trigger[operation as keyof Trigger] : [],
    };
  }
}

/**
   @function generateTriggerName
   generate name for Trigger
   @param {object} trigger: trigger object containing chapter, question and answer
   @returns {string}
 */
function generateTriggerName(trigger: {
  chapter: number;
  question: number;
  answer: number;
}): string {
  const name = `${trigger.chapter}.${trigger.question}.${trigger.answer}`;
  return name;
}

// Function to recursively get all ids, ensuring we process all nested triggers' steps
function getAllStepsFromTrigger(
  triggerId: string,
  state: any,
  visited = new Set()
) {
  let steps: any[] = [triggerId];

  if (visited.has(triggerId)) return steps; // Avoid processing the same trigger again (preventing infinite loops)
  visited.add(triggerId);

  const trigger = state[triggerId];
  if (trigger && trigger.steps) {
    trigger.steps.forEach((step: string) => {
      // If the step is a trigger, we need to recursively get its steps
      if (state[step]) {
        // It's a trigger, so call recursively
        steps = steps.concat(getAllStepsFromTrigger(step, state, visited));
      } else {
        // It's not a trigger, just add it
        steps.push(step);
      }
    });
  }

  return steps;
}

/**
   @function iterateModuleAndReturnSteps
   iterate module and return new selected steps
*/
export function iterateModuleAndReturnSteps(
  selectedTriggers: string[],
  selectedStep: string | undefined,
  state: AppState
) {
  if (!state.moduleState) return;
  let stepsToCheck: any[] = [];
  selectedTriggers.map((trigger) => {
    stepsToCheck = [
      ...stepsToCheck,
      ...(state.moduleState &&
      trigger in state.moduleState.initialization.triggers
        ? state.moduleState.initialization.triggers[trigger].steps.map(
            (step) => {
              return { trigger: trigger, step: step };
            }
          )
        : []),
    ];
  });

  if (
    selectedStep &&
    selectedStep in state.moduleState.initialization.triggers
  ) {
    stepsToCheck = [
      ...stepsToCheck,
      ...state.moduleState.initialization.triggers[selectedStep].steps.map(
        (step) => {
          return { trigger: selectedStep, step: step };
        }
      ),
    ];
  }

  stepsToCheck = stepsToCheck.filter(function (step, i) {
    return stepsToCheck.indexOf(step) === i;
  });

  stepsToCheck = stepsToCheck.filter(
    (value, index, self) =>
      index ===
      self.findIndex(
        (t) => t.step === value.step && t.trigger === value.trigger
      )
  );

  let resetState = false;
  state.moduleState.initialization.selectedSteps.map((step) => {
    if (step.step === selectedStep && step.iterated) {
      resetState = true;
    }
  });

  if (resetState) {
    const selectedStepIndex =
      state.moduleState.initialization.selectedSteps.findIndex(
        (obj) => obj.step === selectedStep
      );

    const previousSteps = state.moduleState.initialization.selectedSteps.slice(
      0,
      selectedStepIndex
    );

    state.toBePrinted = [];
    // @ts-ignore
    state.moduleState = { ...initializeStep(state.module) };

    previousSteps.map((el) => {
      if (el.answers !== undefined) {
        iterateModuleAndReturnSteps(el.answers, el.step, state);
      }
    });
  }

  if (!state.moduleState) return;

  state.moduleState.initialization.selectedSteps.map((step) => {
    if (step.step === selectedStep) {
      step.iterated = true;
      step.answers = selectedTriggers;
    }

    return step;
  });

  selectedTriggers.forEach((trigger) => {
    if (
      state.moduleState &&
      trigger in state.moduleState.initialization.triggers
    ) {
      state.moduleState.initialization.triggers[trigger].selected = true;
    }
  });

  if (stepsToCheck.length > 0) {
    stepsToCheck.forEach(({ step, trigger }) => {
      if (!state.moduleState) return;
      const isValid = validateCondition(
        state.moduleState.initialization.steps[step].trigger,
        trigger
      );

      const stepPos = state.moduleState.map.questions[step];

      if (!isValid && state.moduleState.initialization.steps[step].selected) {
        const data = getAllStepsFromTrigger(
          step,
          state?.moduleState.initialization.triggers
        );
        state.moduleState.initialization.selectedSteps =
          state.moduleState.initialization.selectedSteps.filter((p) => {
            // Only keep the steps that are NOT in the removable steps list
            return !data.includes(p.step);
          });
        state.toBePrinted = state.toBePrinted.filter((p) => {
          // Only keep the steps that are NOT in the removable steps list
          return !data.includes(p.id);
        });
        state.moduleState.initialization.allSelectedSteps =
          state.moduleState.initialization.allSelectedSteps.filter((p) => {
            // Only keep the steps that are NOT in the removable steps list
            return !data.includes(p.step);
          });
        state.moduleState.initialization.steps[step].selected = false;
      }

      if (
        state.module &&
        isValid &&
        !state.moduleState.initialization.steps[step].selected
      ) {
        if (
          !state.module.chapters[stepPos.chapterPos].questions[
            stepPos.questionPos
          ].should_be_hidden
        ) {
          state.moduleState.initialization.selectedSteps.push({
            step: step,
            iterated: false,
          });
        }
        state.moduleState.initialization.allSelectedSteps.push({
          step: step,
          iterated: false,
          hidden:
            state.module.chapters[stepPos.chapterPos].questions[
              stepPos.questionPos
            ].should_be_hidden,
        });
        state.moduleState.initialization.steps[step].selected = true;
      }

      if (
        state.module &&
        state.module.chapters[stepPos.chapterPos].questions[stepPos.questionPos]
          .conclusion &&
        state.module.chapters[stepPos.chapterPos].questions[stepPos.questionPos]
          .print &&
        isValid
      ) {
        if (!state.toBePrinted) state.toBePrinted = [];

        let alreadyIn = false;
        state.toBePrinted.forEach((p) => {
          if (
            state.module &&
            p.id ===
              state.module.chapters[stepPos.chapterPos].questions[
                stepPos.questionPos
              ].question_id
          ) {
            alreadyIn = true;
          }
        });

        if (!alreadyIn) {
          state.toBePrinted.push({
            id: state.module.chapters[stepPos.chapterPos].questions[
              stepPos.questionPos
            ].question_id,
            content:
              state.module.chapters[stepPos.chapterPos].questions[
                stepPos.questionPos
              ].content,
            order:
              state.module.chapters[stepPos.chapterPos].questions[
                stepPos.questionPos
              ].order_number,
          });
        }

        const map = state.moduleState.map.questions;
        const moduleObject = state.module;
        state.toBePrinted.sort((prev, next) => {
          if (prev.step === "toPrint") {
            return 1;
          }

          if (next.step === "toPrint") {
            return -1;
          }

          // @ts-ignore
          const prevPos = Object.keys(map[prev.id]).map((k) => map[prev.id][k]);
          prevPos[1] =
            moduleObject.chapters[prevPos[0]].questions[
              prevPos[1]
            ].order_number;
          prevPos[0] = moduleObject.chapters[prevPos[0]].order_number;

          // @ts-ignore
          const nextPos = Object.keys(map[next.id]).map((k) => map[next.id][k]);

          nextPos[1] =
            moduleObject.chapters[nextPos[0]].questions[
              nextPos[1]
            ].order_number;
          nextPos[0] = moduleObject.chapters[nextPos[0]].order_number;

          const result = 0;

          for (let i = 0; i < prevPos.length; i++) {
            if (prevPos[i] > nextPos[i]) {
              return 1;
            } else if (prevPos[i] < nextPos[i]) {
              return -1;
            }
          }

          return result;
        });
      }
    });

    if (!state.moduleState) return;

    state.moduleState.initialization.allSelectedSteps = sortSelectedSteps(
      state.moduleState.initialization.allSelectedSteps,
      state.moduleState.map.questions,
      state.module
    );

    state.moduleState.initialization.selectedSteps = sortSelectedSteps(
      state.moduleState.initialization.selectedSteps,
      state.moduleState.map.questions,
      state.module
    );

    let beforeIt = "";

    state.moduleState.initialization.selectedSteps.map((step, i) => {
      if (state.moduleState && step.step === selectedStep) {
        step.iterated = true;
        // @ts-ignore
        beforeIt = state.moduleState.initialization.selectedSteps[i + 1].step;
      }

      return step;
    });

    let toCheckBeforeIt = 0;
    state.moduleState.initialization.allSelectedSteps.forEach((s, i) => {
      // @ts-ignore
      if (s === beforeIt) {
        toCheckBeforeIt = i;
      }
    });

    state.moduleState.initialization.allSelectedSteps.map((step, i) => {
      if (toCheckBeforeIt < i && !step.iterated && step.hidden) {
        step.iterated = true;

        iterateModuleAndReturnSteps([], step.step, state);
      }
    });

    state.moduleState.initialization.allSelectedSteps = sortSelectedSteps(
      state.moduleState.initialization.allSelectedSteps,
      state.moduleState.map.questions,
      state.module
    );

    state.moduleState.initialization.selectedSteps = sortSelectedSteps(
      state.moduleState.initialization.selectedSteps,
      state.moduleState.map.questions,
      state.module
    );
  }
}

/*
  @function sortSelectedSteps
  @param list {array} - list of steps
  @param map {string} - map of Module,
  @param module {object} - module object
  @returns {array}
 */
function sortSelectedSteps(
  list: SelectedStep[],
  map: QuestionMap,
  moduleObject: Module | null
) {
  if (moduleObject === null) {
    return list;
  }
  list.sort((prev, next) => {
    if (prev.step === "toPrint") {
      return 1;
    }

    if (next.step === "toPrint") {
      return -1;
    }

    // @ts-ignore
    const prevPos = Object.keys(map[prev.step]).map((k) => map[prev.step][k]);
    prevPos[1] =
      moduleObject.chapters[prevPos[0]].questions[prevPos[1]].order_number;
    prevPos[0] = moduleObject.chapters[prevPos[0]].order_number;

    // @ts-ignore
    const nextPos = Object.keys(map[next.step]).map((k) => map[next.step][k]);

    nextPos[1] =
      moduleObject.chapters[nextPos[0]].questions[nextPos[1]].order_number;
    nextPos[0] = moduleObject.chapters[nextPos[0]].order_number;

    const result = 0;

    for (let i = 0; i < prevPos.length; i++) {
      if (prevPos[i] > nextPos[i]) {
        return 1;
      } else if (prevPos[i] < nextPos[i]) {
        return -1;
      }
    }

    return result;
  });
  return list;
}
